import { AuthUserDTO } from '@sandbox-platform/shared-ui';
import {
  AuthState,
  CompanyContextDTO,
  CompanySelectionResponseDTO,
  LoginFlowResponseDTO,
} from '../types/auth.types';

// Constants
export const COMPANY_COOKIE_KEY = 'current_company';
export const COMPANY_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// Types
export type LoginResult =
  | { status: 'success' }
  | { status: 'company-selection-required'; companies: CompanyContextDTO[] };

// Initial state
export const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  tokens: {
    access_token: null,
    refresh_token: null,
    expires_in: null,
  },
  sessionId: null,
  mustResetPassword: false,
  currentCompany: null,
};

export const STORAGE_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  expiresIn: 'expires_in',
  user: 'user',
  sessionId: 'session_id',
  currentCompany: 'current_company',
} as const;

export const computeMustReset = (responseFlag?: boolean, userFlag?: boolean) =>
  responseFlag ?? userFlag ?? false;

export const enrichUserWithClaims = (
  baseUser: AuthUserDTO,
  mustReset: boolean,
  jwtPayload: Record<string, any> | null
): AuthUserDTO => ({
  ...baseUser,
  must_reset_password: mustReset,
  svc_roles: jwtPayload?.['svc_roles'] ?? {},
  svc_perms: jwtPayload?.['svc_perms'] ?? {},
  svc_scopes: jwtPayload?.['svc_scopes'] ?? {},
});

/**
 * Persist tokens to localStorage
 * @param tokens - { access_token: string, refresh_token: string, expires_in: number }
 */
export const persistTokens = (tokens: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}) => {
  localStorage.setItem(STORAGE_KEYS.accessToken, tokens.access_token);
  localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refresh_token);
  localStorage.setItem(STORAGE_KEYS.expiresIn, tokens.expires_in.toString());
};

/**
 * Persist user to localStorage
 * @param user - AuthUserDTO | null
 */
export const persistUser = (user: AuthUserDTO | null) => {
  if (!user) {
    localStorage.removeItem(STORAGE_KEYS.user);
    return;
  }

  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
};

/**
 * Persist session ID to localStorage
 * @param sessionId - string | null
 */
export const persistSession = (sessionId: string | null) => {
  if (sessionId) {
    localStorage.setItem(STORAGE_KEYS.sessionId, sessionId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.sessionId);
  }
};

/**
 * Persist company context to cookie
 * @param company - CompanyContextDTO | null
 */
export const persistCompanyCookie = (company: CompanyContextDTO | null) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (company) {
    document.cookie = `${COMPANY_COOKIE_KEY}=${encodeURIComponent(
      JSON.stringify(company)
    )};path=/;max-age=${COMPANY_COOKIE_MAX_AGE};samesite=lax`;
  } else {
    document.cookie = `${COMPANY_COOKIE_KEY}=;path=/;max-age=0;samesite=lax`;
  }
};

export const clearPersistedAuth = () => {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.expiresIn);
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.sessionId);
  localStorage.removeItem(STORAGE_KEYS.currentCompany);
  persistCompanyCookie(null);
};

/**
 * Read company context from cookie
 * @returns CompanyContextDTO | null
 */
export const readCompanyCookie = (): CompanyContextDTO | null => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COMPANY_COOKIE_KEY}=`));

  if (!cookie) {
    return null;
  }

  const value = decodeURIComponent(cookie.split('=')[1] ?? '');

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as CompanyContextDTO;
  } catch (error) {
    console.error('Failed to parse company context cookie:', error);
    document.cookie = `${COMPANY_COOKIE_KEY}=;path=/;max-age=0;samesite=lax`;
    return null;
  }
};

/**
 * Decode JWT token payload (client-side for reading claims only)
 * NOTE: This is for reading JWT claims only. JWT validation happens server-side.
 * @param token - JWT access token
 * @returns Decoded JWT payload or null if invalid
 */
export const decodeJWT = (token: string): any => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format: expected 3 parts');
      return null;
    }

    // Decode the payload (second part)
    // Replace URL-safe characters with standard base64 characters
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Type guard to check if response requires company selection
 */
export const isCompanySelectionResponse = (
  payload: LoginFlowResponseDTO
): payload is CompanySelectionResponseDTO => {
  return (
    (payload as CompanySelectionResponseDTO)?.company_selection_required ===
      true && Array.isArray((payload as CompanySelectionResponseDTO).companies)
  );
};
